'use client';

import { Menu as BaseMenu } from '@base-ui/react/menu';
import { useCallback, useMemo, useState } from 'react';

import { type Component, type ComponentProps } from '../../types/component';
import { MenuSingleSelectGroupContext } from './internal/MenuSingleSelectGroupContext';

interface MenuSingleSelectGroupBaseProps {
  /**
   * The current selected value (controlled).
   */
  value?: string;
  /**
   * The default selected value (uncontrolled).
   */
  defaultValue?: string;
  /**
   * Handler that is called when the selected value changes.
   */
  onValueChange?: (value: string) => void;
  /**
   * Disables all items in the group.
   *
   * @default false
   */
  isDisabled?: boolean;
}

type MenuSingleSelectGroupProps = ComponentProps<
  MenuSingleSelectGroupBaseProps,
  'div'
>;

type MenuSingleSelectGroupComponent = Component<
  MenuSingleSelectGroupBaseProps,
  'div'
>;

const MenuSingleSelectGroup: MenuSingleSelectGroupComponent = (props) => {
  const {
    value,
    defaultValue,
    onValueChange,
    isDisabled = false,
    children,
    ref,
    ...rest
  } = props;

  const [trackedValue, setTrackedValue] = useState(defaultValue);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setTrackedValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );

  const resolvedValue = value ?? trackedValue;

  const groupContext = useMemo(
    () => ({ value: resolvedValue, isDisabled }),
    [resolvedValue, isDisabled]
  );

  return (
    <MenuSingleSelectGroupContext.Provider value={groupContext}>
      <BaseMenu.RadioGroup
        {...rest}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        className="col-span-3 grid grid-cols-subgrid"
      >
        {children}
      </BaseMenu.RadioGroup>
    </MenuSingleSelectGroupContext.Provider>
  );
};

MenuSingleSelectGroup.displayName = 'Menu.SingleSelectGroup';

export {
  MenuSingleSelectGroup,
  type MenuSingleSelectGroupProps,
  type MenuSingleSelectGroupBaseProps,
};
